/**
 * Geographic Convergence Detection — identifies 1-degree grid cells
 * where 3+ distinct event types converge within a 24-hour window.
 *
 * Algorithm:
 * 1. Divide world into 1-degree lat/lng grid cells
 * 2. For events in last 24 hours with location data, assign to grid cells
 * 3. Convergence flagged when cell has 3+ distinct event types
 * 4. Score: typeCount * 25 + min(totalEvents * 2, 50), normalized 0-100
 * 5. Label cells using conflict zones, chokepoints, country centroids, or coordinates
 */

import type { NormalizedEvent } from '@/sources/base-source';
import type { ConvergenceCell } from './types';
import { getEventTimestamp } from './types';
import { haversineDistance } from '@/utils/geo';
import { getCountryCentroid } from '@/config/country-centroids';

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
const MIN_EVENT_TYPES = 3;

// Known active conflict zones for cell labeling: [name, lat, lng]
const CONFLICT_ZONES: [string, number, number][] = [
  ['Eastern Ukraine', 48.5, 37.5],
  ['Crimea', 45.3, 34.1],
  ['Syria', 35.0, 38.0],
  ['Yemen', 15.5, 44.0],
  ['Myanmar', 19.0, 96.0],
  ['Somalia', 5.0, 45.0],
  ['Northern Ethiopia', 12.5, 39.5],
  ['Sudan', 15.0, 32.0],
  ['South Sudan', 7.0, 30.0],
  ['Sahel Region', 15.0, 0.0],
  ['Northern Mozambique', -13.0, 40.0],
  ['Eastern DR Congo', -2.0, 28.0],
  ['Iraq', 34.0, 44.0],
  ['Afghanistan', 34.0, 66.0],
  ['Kashmir', 34.5, 75.5],
  ['Gaza', 31.4, 34.4],
  ['Southern Lebanon', 33.3, 35.5],
  ['Libya', 31.0, 17.0],
  ['Central African Republic', 6.5, 20.0],
  ['Burkina Faso', 12.0, -1.5],
];

// Maritime chokepoints for cell labeling: [name, lat, lng]
const CHOKEPOINTS: [string, number, number][] = [
  ['Strait of Hormuz', 26.5, 56.3],
  ['Bab el-Mandeb', 12.6, 43.3],
  ['Strait of Malacca', 2.5, 101.5],
  ['Suez Canal', 30.5, 32.3],
  ['Panama Canal', 9.1, -79.7],
  ['Turkish Straits', 41.0, 29.0],
  ['Strait of Gibraltar', 35.9, -5.6],
  ['Taiwan Strait', 24.0, 119.5],
  ['Danish Straits', 55.7, 12.0],
  ['Mozambique Channel', -17.0, 42.0],
];

// Country display names for labeling (priority countries)
const COUNTRY_DISPLAY: Record<string, string> = {
  US: 'United States', CN: 'China', RU: 'Russia', UA: 'Ukraine',
  IL: 'Israel', IR: 'Iran', KP: 'North Korea', TW: 'Taiwan',
  IN: 'India', PK: 'Pakistan', SA: 'Saudi Arabia', GB: 'United Kingdom',
  FR: 'France', DE: 'Germany', JP: 'Japan', KR: 'South Korea',
  AU: 'Australia', BR: 'Brazil', NG: 'Nigeria', ZA: 'South Africa',
  TR: 'Turkey', EG: 'Egypt', ID: 'Indonesia', MX: 'Mexico',
  PH: 'Philippines', TH: 'Thailand', VN: 'Vietnam', PL: 'Poland',
  IT: 'Italy', ES: 'Spain',
};

/**
 * Label a grid cell using hierarchical naming:
 * 1. Known conflict zones within 300km
 * 2. Maritime chokepoints within 200km
 * 3. Country centroids within 500km (priority countries)
 * 4. Fallback: coordinate-based label
 */
function labelCell(lat: number, lng: number): string {
  // Check conflict zones (300km radius)
  for (const [name, zLat, zLng] of CONFLICT_ZONES) {
    if (haversineDistance(lat, lng, zLat, zLng) < 300) return name;
  }

  // Check maritime chokepoints (200km radius)
  for (const [name, cLat, cLng] of CHOKEPOINTS) {
    if (haversineDistance(lat, lng, cLat, cLng) < 200) return name;
  }

  // Check country centroids (500km radius, priority countries first)
  for (const [code, displayName] of Object.entries(COUNTRY_DISPLAY)) {
    const centroid = getCountryCentroid(code);
    if (centroid && haversineDistance(lat, lng, centroid[0], centroid[1]) < 500) {
      return displayName;
    }
  }

  // Coordinate-based fallback
  const latDir = lat >= 0 ? 'N' : 'S';
  const lngDir = lng >= 0 ? 'E' : 'W';
  return `Region ${Math.abs(lat).toFixed(0)}\u00B0${latDir} ${Math.abs(lng).toFixed(0)}\u00B0${lngDir}`;
}

/**
 * Detect geographic convergence — grid cells with 3+ distinct event types
 * within the last 24 hours.
 */
export function detectConvergence(events: NormalizedEvent[]): ConvergenceCell[] {
  const cutoff = Date.now() - TWENTY_FOUR_HOURS_MS;

  // Filter to recent events with valid location data
  const recent = events.filter((e) => {
    if (e.location?.lat == null || e.location?.lng == null) return false;
    return getEventTimestamp(e) >= cutoff;
  });

  if (recent.length === 0) return [];

  // Assign events to 1-degree grid cells
  const cells = new Map<
    string,
    { types: Set<string>; count: number; lat: number; lng: number }
  >();

  for (const ev of recent) {
    const lat = Math.floor(ev.location!.lat);
    const lng = Math.floor(ev.location!.lng);
    const key = `${lat}:${lng}`;

    if (!cells.has(key)) {
      // Cell center = floor + 0.5
      cells.set(key, { types: new Set(), count: 0, lat: lat + 0.5, lng: lng + 0.5 });
    }
    const cell = cells.get(key)!;
    cell.types.add(ev.type);
    cell.count++;
  }

  // Filter to cells meeting the convergence threshold
  const convergences: ConvergenceCell[] = [];

  for (const [, cell] of cells) {
    const typeCount = cell.types.size;
    if (typeCount < MIN_EVENT_TYPES) continue;

    const rawScore = typeCount * 25 + Math.min(cell.count * 2, 50);
    const score = Math.min(100, rawScore);

    convergences.push({
      lat: cell.lat,
      lng: cell.lng,
      eventTypes: Array.from(cell.types),
      totalEvents: cell.count,
      score,
      label: labelCell(cell.lat, cell.lng),
    });
  }

  // Sort by score descending (highest convergence first)
  convergences.sort((a, b) => b.score - a.score);

  return convergences;
}
